import { column, Css, emptyCell, GridColumn, GridDataRow, GridTable, simpleHeader } from "@homebound/beam";
import { priceCell } from "src/components/gridTableCells";
import {
  PlanBasedLineItemsTab_LineItemFragment,
  PlanBasedLineItemsTab_PlanBasedLineItemFragment,
} from "src/generated/graphql-types";

type PlanBasedBidLinesTableProps = {
  planBasedLineItems: PlanBasedLineItemsTab_PlanBasedLineItemFragment[];
  lineItems: PlanBasedLineItemsTab_LineItemFragment[];
};

export function PlanBasedBidLinesTable(props: PlanBasedBidLinesTableProps) {
  const { planBasedLineItems, lineItems } = props;
  return (
    <section data-testid="planBasedBidLinesSection">
      <div css={Css.lgSb.mb4.$}>Cost Lines</div>
      <GridTable
        columns={columns}
        rows={createRows(planBasedLineItems, lineItems)}
        style={{ bordered: false, allWhite: true }}
        rowStyles={{
          header: { cellCss: Css.gray600.$ },
          total: { cellCss: Css.smSb.mt2.$ },
        }}
        fallbackMessage="No plan based bid lines"
      />
    </section>
  );
}

type HeaderRow = { kind: "header" };
type DataRow = { kind: "data"; data: PlanBasedLineItemsTab_PlanBasedLineItemFragment & { taxableCostInCents: number } };
type TotalRow = { kind: "total"; data: { totalCostInCents: number } };
type Row = HeaderRow | DataRow | TotalRow;

const columns: GridColumn<Row>[] = [
  column<Row>({ header: "Option", data: ({ groupLine }) => groupLine.group.displayName, total: emptyCell }),
  column<Row>({
    header: "Dimensions",
    data: ({ groupLine }) => groupLine.options.map((o) => o.name).join(" "),
    total: emptyCell,
  }),
  column<Row>({
    header: "Cost",
    align: "right",
    data: ({ taxableCostInCents }) => priceCell({ valueInCents: taxableCostInCents }),
    total: ({ totalCostInCents }) => priceCell({ id: "totalCostInCents", valueInCents: totalCostInCents }),
  }),
];

function createRows(
  planBasedLineItems: PlanBasedLineItemsTab_PlanBasedLineItemFragment[],
  lineItems: PlanBasedLineItemsTab_LineItemFragment[],
): GridDataRow<Row>[] {
  const rows = planBasedLineItems.map((pbl) => {
    const matching = lineItems.filter((cli) => cli.primaryBidContractLineItem?.id === pbl.id);
    return {
      kind: "data" as const,
      id: pbl.id,
      data: { ...pbl, taxableCostInCents: matching.sum((cli) => cli.taxableCostInCents) },
    };
  });
  return [
    simpleHeader,
    ...rows,
    { id: "total", kind: "total" as const, data: { totalCostInCents: rows.sum((r) => r.data.taxableCostInCents) } },
  ];
}
